import {computed, makeObservable} from "mobx";

import {ClientUser} from '../../API';
import {DataSource, FilterSchema} from "./DataSource";

import {listClientUsers} from "./requests/listClientUsers";
import {getColor} from "../../utils/stringToColor";

import {onCreateClientUser, onDeleteClientUser, onUpdateClientUser} from "../../graphql/subscriptions";
import { UserDataSourceDeps } from "../../platform/services/types/DataSourceDependencies";

export interface Color {
    hue: number;
    saturation: number;
    lightness: number;
    alpha?: number;
}

export class UsersDataSource extends DataSource<ClientUser, ClientUser, UserDataSourceDeps> {
    readonly _modelName = 'ClientUser' as const;
    readonly _subscriptionGQLs = {
        onCreate: onCreateClientUser,
        onUpdate: onUpdateClientUser,
        onDelete: onDeleteClientUser,
    } as const;

    constructor(deps: UserDataSourceDeps) {
        super(deps);
        makeObservable({
            users: computed,
            activeUsers: computed,
            activeUsersWithoutOwner: computed,
        })
    }

    get syncHash() {
        return '';
    }

    get users() {
        const users = new Map();
        for (const user of this.allRows) {
            users.set(user.id, user);
        }
        return users;
    }

    get activeUsers() {
        return Array.from(this.users.values())
            .filter(user => !user.closedAt)
            .map(user => this.users.get(user.id)!);
    }

    get activeUsersWithoutOwner() {
        return this.activeUsers
            .filter(user => user.id !== this.deps.userState?.ownerUserId)
    }

    getUserByEmail = (email: string) => {
        for (const [id, user] of this.users.entries()) {
            if (user.email === email) {
                return this.users.get(id);
            }
        }
        return null;
    }

    getUserBySub = (sub: string) => {
        for (const [id, user] of this.users.entries()) {
            if (user.sub === sub) {
                return this.users.get(id);
            }
        }
        return null;
    }

    getUserColor = (userId?: string, alpha?: number) => {
        if (!userId) {
            return 'gray';
        }
        const user = this.users.get(userId);
        if (!user) {
            return 'gray';
        }
        const userColor = getColor(user);
        if (alpha) {
            return `hsl(${userColor.hue}, ${userColor.saturation}%, ${userColor.lightness}%, ${alpha})`;
        } else {
            return `hsl(${userColor.hue}, ${userColor.saturation}%, ${userColor.lightness}%)`;
        }
    }

    getFilterDefaultValues(): {} {
        return {}
    }

    getFilterSchema(): FilterSchema<ClientUser> {
        return {
            name: {
                type: 'client',
                apply: (clientUser: ClientUser) => clientUser.name?.toLowerCase().indexOf(this.filterValues.name.toLowerCase()) !== -1,
            },
        }
    }

    loadGQLFetch() {
        return listClientUsers(
            this.deps.dataClientService,
            this.deps.authService
        );
    };

    loadMoreGQLFetch(nextToken: string) {
        return listClientUsers(
            this.deps.dataClientService,
            this.deps.authService,
            nextToken
        );
    };

    getWsClient() {
        return this.deps.dataClientService.getWsClientForTable('clientUsers');
    };
}
